import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react'; // Import useEffect
import { CssBaseline } from '@mui/material';
import { alpha, ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import { ColorModeContext } from './ColorModeContext';

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const [mode, setMode] = useState(localStorage.getItem('theme') || 'light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setMode(savedTheme);
    }
  }, []);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
        localStorage.setItem('theme', mode === 'light' ? 'dark' : 'light');
      },
    }),
    [mode]
  );

  const theme = useMemo(() => {
    // SETUP COLORS
    const GREY = {
      0: '#FFFFFF',
      100: '#F9FAFB',
      200: '#F4F6F8',
      300: '#DFE3E8',
      400: '#C4CDD5',
      500: '#919EAB',
      600: '#637381',
      700: '#454F5B',
      800: '#212B36',
      900: '#161C24',
    };

    const PRIMARY = {
      lighter: '#D1E9FC',
      light: '#76B0F1',
      main: '#2065D1',
      dark: '#103996',
      darker: '#061B64',
      contrastText: '#fff',
      // Dark Mode Colors
      darkMode: {
        lighter: '#32598B',
        light: '#1F406D',
        main: '#0E1E38',
        dark: '#070D1F',
        darker: '#03060A',
        contrastText: '#fff',
      },
    };

    const SECONDARY = {
      lighter: '#D6E4FF',
      light: '#84A9FF',
      main: '#3366FF',
      dark: '#1939B7',
      darker: '#091A7A',
      contrastText: '#fff',
    };

    const INFO = {
      lighter: '#D0F2FF',
      light: '#74CAFF',
      main: '#1890FF',
      dark: '#0C53B7',
      darker: '#04297A',
      contrastText: '#fff',
    };

    const SUCCESS = {
      lighter: '#E9FCD4',
      light: '#AAF27F',
      main: '#54D62C',
      dark: '#229A16',
      darker: '#08660D',
      contrastText: GREY[800],
    };

    const WARNING = {
      lighter: '#FFF7CD',
      light: '#FFE16A',
      main: '#FFC107',
      dark: '#B78103',
      darker: '#7A4F01',
      contrastText: GREY[800],
    };

    const ERROR = {
      lighter: '#FFE7D9',
      light: '#FFA48D',
      main: '#FF4842',
      dark: '#B72136',
      darker: '#7A0C2E',
      contrastText: '#fff',
    };

    return createTheme({
      palette: {
        mode,
        common: { black: '#000', white: '#fff' },
        primary: PRIMARY,
        secondary: SECONDARY,
        info: INFO,
        success: SUCCESS,
        warning: WARNING,
        error: ERROR,
        grey: GREY,
        divider: alpha(GREY[500], 0.24),
        background:
          mode === 'light' ? { paper: '#f0f0f0', default: '#f0f0f0' } : { paper: '#34383a', default: '#34383a' },
      },
      shape: { borderRadius: 6 },
      typography,
      componentsOverride,
      shadows: shadows(),
      customShadows: customShadows(),
    });
  }, [mode]);

  return (
    <StyledEngineProvider injectFirst>
      <ColorModeContext.Provider value={colorMode}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          {children}
        </MUIThemeProvider>
      </ColorModeContext.Provider>
    </StyledEngineProvider>
  );
}
